import { motion, AnimatePresence } from 'framer-motion';
import { X, Building2, Landmark } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProfileCompletionModal({ isOpen, onClose }: ProfileCompletionModalProps) {
  const navigate = useNavigate();

  const handleChoice = (choice: 'company' | 'institution' | 'later') => {
    if (choice === 'company') {
      navigate('/company');
      onClose();
    } else if (choice === 'institution') {
      navigate('/financial-institution');
      onClose();
    } else {
      // "Rappelez-moi plus tard" - simply close
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div
              className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            {/* Content */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Complétez votre profil
              </h2>
              <p className="text-gray-600 text-lg">
                Quel type de profil souhaitez-vous créer ?
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* PME & Startup Option */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleChoice('company')}
                className="group relative overflow-hidden rounded-xl border-2 border-gray-200 p-6 hover:border-primary hover:shadow-lg transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <Building2 className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    PME & Startup
                  </h3>
                  <p className="text-sm text-gray-600">
                    Créez votre profil d'entreprise pour accéder aux services de financement et de gestion
                  </p>
                </div>
              </motion.button>

              {/* Financial Institution Option */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleChoice('institution')}
                className="group relative overflow-hidden rounded-xl border-2 border-gray-200 p-6 hover:border-warning hover:shadow-lg transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center mb-4 group-hover:bg-warning/20 transition-colors">
                    <Landmark className="w-8 h-8 text-warning" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Institution Financière
                  </h3>
                  <p className="text-sm text-gray-600">
                    Créez votre profil institutionnel pour proposer vos services de financement
                  </p>
                </div>
              </motion.button>
            </div>

            {/* Remind me later button */}
            <button
              onClick={() => handleChoice('later')}
              className="w-full py-3 px-6 text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Rappelez-moi plus tard
            </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
